import { useId, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardDescription,
  CardHeader,
  CardTitle,
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  ProgressBar,
} from '@ludus/ui'
import type { AuditsApiError } from '../api/index.ts'
import { useDecide, useReviewQueue } from '../hooks/index.ts'
import type { ReviewRow } from '../types.ts'
import { formatDate } from '../utils/format.ts'

/** Shown in the needs-decision group whatever the threshold (ruling 2026-08-22). */
const ALWAYS_DECIDE = new Set(['call_only_lead'])
const DEFAULT_THRESHOLD = 0.6

/**
 * The review queue with its confidence threshold (§6.4, §10.3, ruling
 * 2026-08-22): rows the rules were sure enough about sit above the line as
 * auto-accepted; the rest — and every call-only cycle — need a decision. A
 * decision needs a reason and goes through the same facade method the MCP
 * `review_queue.resolve` calls, so console and session share one journal.
 */
export function ReviewView({ auditId }: { auditId: string }) {
  const { t } = useTranslation()
  const { items, budget, error, isLoading } = useReviewQueue(auditId)
  const [threshold, setThreshold] = useState(DEFAULT_THRESHOLD)
  const [deciding, setDeciding] = useState<{
    row: ReviewRow
    decision: 'accept' | 'reject'
  } | null>(null)

  const open = items.filter((row) => row.state === 'open')
  const needsDecision = open.filter(
    (row) => ALWAYS_DECIDE.has(row.kind) || row.confidence === null || row.confidence < threshold,
  )
  const autoAccepted = open.filter((row) => !needsDecision.includes(row))
  const resolved = items.filter((row) => row.state === 'resolved')

  return (
    <div className="flex w-full max-w-6xl flex-1 flex-col gap-6 p-4 sm:p-6">
      <header className="flex flex-wrap items-end justify-between gap-3 border-b border-border/60 pb-4">
        <div className="flex flex-col gap-1">
          <nav className="text-xs text-muted">
            <Link to={`/audits/${auditId}`} className="hover:underline">
              {t('pages.audits.card.backToAudit', { defaultValue: 'Audit' })}
            </Link>
          </nav>
          <h1 className="text-xl font-semibold text-foreground">
            {t('pages.audits.review.title', { defaultValue: 'Review queue' })}
          </h1>
          <p className="text-xs text-muted">
            {t('pages.audits.review.subtitle', {
              defaultValue:
                'Every doubt the passes filed, with the proposal the rules would have applied.',
            })}
          </p>
        </div>
        {budget && (
          <div className="flex min-w-[14rem] flex-col gap-1 text-xs">
            <span className="text-muted">
              {t('pages.audits.review.budget', {
                defaultValue: '{{spent}} h of {{hours}} h spent · {{decisions}} decisions',
                spent: budget.spentHours,
                hours: budget.hours,
                decisions: budget.decisions,
              })}
            </span>
            <ProgressBar
              value={
                budget.hours === 0 ? 0 : Math.min(100, (budget.spentHours / budget.hours) * 100)
              }
              tone={budget.spentHours >= budget.hours ? 'danger' : 'primary'}
              label={t('pages.audits.review.budgetGauge', { defaultValue: 'Review budget' })}
            />
          </div>
        )}
      </header>

      {error && (
        <p role="alert" className="text-xs text-toast-error-text">
          {error.message}
        </p>
      )}
      {isLoading && (
        <p className="text-xs text-muted">
          {t('pages.audits.review.loading', { defaultValue: 'Loading the queue…' })}
        </p>
      )}

      <label className="flex flex-col gap-1 text-xs">
        <span className="text-muted">
          {t('pages.audits.review.threshold', {
            defaultValue:
              'Confidence threshold: {{value}} — above it the rules’ answer stands, below it a person decides',
            value: threshold.toFixed(2),
          })}
        </span>
        <input
          type="range"
          min={0}
          max={1}
          step={0.05}
          value={threshold}
          onChange={(e) => setThreshold(Number(e.target.value))}
          aria-label={t('pages.audits.review.thresholdLabel', {
            defaultValue: 'Confidence threshold',
          })}
        />
      </label>

      <Group
        title={t('pages.audits.review.groups.needsDecision', { defaultValue: 'Needs a decision' })}
        description={t('pages.audits.review.groups.needsDecisionBody', {
          defaultValue: 'Below the threshold, without a confidence, or a call-only cycle — always.',
        })}
        rows={needsDecision}
        auditId={auditId}
        onDecide={(row, decision) => setDeciding({ row, decision })}
      />
      <Group
        title={t('pages.audits.review.groups.autoAccepted', {
          defaultValue: 'Auto-accepted by the threshold',
        })}
        description={t('pages.audits.review.groups.autoAcceptedBody', {
          defaultValue: 'The rules were sure enough; a decision still pins the answer.',
        })}
        rows={autoAccepted}
        auditId={auditId}
        onDecide={(row, decision) => setDeciding({ row, decision })}
      />
      <Group
        title={t('pages.audits.review.groups.resolved', { defaultValue: 'Resolved' })}
        rows={resolved}
        auditId={auditId}
      />

      {deciding && (
        <DecisionDialog
          auditId={auditId}
          row={deciding.row}
          decision={deciding.decision}
          onClose={() => setDeciding(null)}
        />
      )}
    </div>
  )
}

function Group({
  title,
  description,
  rows,
  auditId,
  onDecide,
}: {
  title: string
  description?: string
  rows: ReviewRow[]
  auditId: string
  onDecide?: (row: ReviewRow, decision: 'accept' | 'reject') => void
}) {
  const { t } = useTranslation()
  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {title} <span className="text-xs font-normal text-muted">({rows.length})</span>
        </CardTitle>
        {description && <CardDescription>{description}</CardDescription>}
      </CardHeader>
      <CardBody>
        {rows.length === 0 ? (
          <p className="text-xs text-muted">
            {t('pages.audits.review.empty', { defaultValue: 'Nothing here.' })}
          </p>
        ) : (
          <ul className="flex flex-col divide-y divide-border/60">
            {rows.map((row) => (
              <li key={row.id} className="flex flex-wrap items-center gap-3 py-2 text-sm">
                <Badge tone="warning" size="sm">
                  {t(`pages.audits.anomalies.kinds.${row.kind}`, { defaultValue: row.kind })}
                </Badge>
                {row.leadId ? (
                  <Link className="hover:underline" to={`/audits/${auditId}/cards/${row.leadId}`}>
                    {row.leadId}
                  </Link>
                ) : (
                  <span className="text-xs text-muted">{row.subjectId}</span>
                )}
                <span className="text-xs text-muted">
                  {t(`pages.audits.review.proposal.${String(row.proposal.action ?? 'none')}`, {
                    defaultValue: String(row.proposal.action ?? '—'),
                  })}
                </span>
                <span className="text-xs text-muted">
                  {row.confidence === null
                    ? t('pages.audits.review.noConfidence', { defaultValue: 'no confidence' })
                    : `${Math.round(row.confidence * 100)}%`}
                </span>
                <span className="text-[11px] text-muted">{formatDate(row.createdAt)}</span>
                {row.state === 'resolved' ? (
                  <span className="ml-auto text-xs text-muted">
                    {t(`pages.audits.review.resolution.${row.resolution ?? 'undone'}`, {
                      defaultValue: row.resolution ?? '',
                    })}
                    {row.resolvedBy ? ` · ${row.resolvedBy}` : ''}
                    {row.reason ? ` · ${row.reason}` : ''}
                  </span>
                ) : (
                  onDecide && (
                    <span className="ml-auto flex gap-2">
                      <Button size="sm" variant="secondary" onClick={() => onDecide(row, 'accept')}>
                        {t('pages.audits.review.resolve', { defaultValue: 'Resolve' })}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => onDecide(row, 'reject')}>
                        {t('pages.audits.review.dismiss', { defaultValue: 'Dismiss' })}
                      </Button>
                    </span>
                  )
                )}
              </li>
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  )
}

function DecisionDialog({
  auditId,
  row,
  decision,
  onClose,
}: {
  auditId: string
  row: ReviewRow
  decision: 'accept' | 'reject'
  onClose: () => void
}) {
  const { t } = useTranslation()
  const formId = useId()
  const [reason, setReason] = useState('')
  const [failure, setFailure] = useState<string | null>(null)
  const { trigger, isMutating } = useDecide(auditId)
  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (reason.trim() === '' || isMutating) return
    setFailure(null)
    try {
      await trigger({ queueId: row.id, decision, reason: reason.trim() })
      onClose()
    } catch (err) {
      setFailure((err as AuditsApiError).message)
    }
  }
  return (
    <Dialog open onOpenChange={(next) => !next && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {decision === 'accept'
              ? t('pages.audits.review.dialog.resolveTitle', { defaultValue: 'Resolve the doubt' })
              : t('pages.audits.review.dialog.dismissTitle', {
                  defaultValue: 'Dismiss the proposal',
                })}
          </DialogTitle>
          <DialogDescription>
            {t('pages.audits.review.dialog.description', {
              defaultValue:
                'A decision needs a reason; it is pinned so a re-run cannot undo it, and undo is a reverse row.',
            })}
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <form id={formId} onSubmit={submit} className="flex flex-col gap-2 text-sm">
            <label className="flex flex-col gap-1">
              <span className="text-xs text-muted">
                {t('pages.audits.review.dialog.reason', { defaultValue: 'Reason' })}
              </span>
              <Input value={reason} onChange={(e) => setReason(e.target.value)} autoFocus />
            </label>
            {reason.trim() === '' && (
              <span className="text-[11px] text-muted">
                {t('pages.audits.review.dialog.reasonRequired', { defaultValue: 'Required.' })}
              </span>
            )}
            {failure && (
              <p role="alert" className="text-xs text-toast-error-text">
                {failure}
              </p>
            )}
          </form>
        </DialogBody>
        <DialogFooter>
          <DialogClose>
            <Button variant="ghost" size="sm" type="button">
              {t('pages.audits.wizard.cancel', { defaultValue: 'Cancel' })}
            </Button>
          </DialogClose>
          <Button
            variant="primary"
            size="sm"
            type="submit"
            form={formId}
            disabled={isMutating || reason.trim() === ''}
          >
            {decision === 'accept'
              ? t('pages.audits.review.resolve', { defaultValue: 'Resolve' })
              : t('pages.audits.review.dismiss', { defaultValue: 'Dismiss' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
